import { useCallback, useState } from 'react';

// ============================================
// TYPES
// ============================================

export interface UseMultiStepFormOptions {
  /** Total number of steps */
  totalSteps: number;
  /**
   * Initial step index (0-based). Clamped to `[0, totalSteps - 1]`. Use for
   * "resume at step N" flows (e.g. Continue Editing → Place Fields) where the
   * caller has already hydrated all prior-step state and wants to skip the
   * usual step-by-step validation. Only honored on first mount — changes are
   * ignored to avoid accidental resets.
   */
  initialStep?: number;
  /**
   * Async validation callback for the current step.
   * Return true to allow advancing, false to block.
   * Consumer wires in RHF trigger() or any other validation.
   */
  onValidateStep?: (stepIndex: number) => Promise<boolean> | boolean;
}

export interface UseMultiStepFormReturn {
  /** Current step index (0-based) */
  currentStep: number;
  /** Whether the current step is the first */
  isFirstStep: boolean;
  /** Whether the current step is the last */
  isLastStep: boolean;
  /** Direction of the last transition: 1 = forward, -1 = backward */
  direction: 1 | -1;
  /** Validate current step fields, then advance */
  goToNext: () => Promise<boolean>;
  /** Go back (no validation needed) */
  goToPrev: () => void;
  /** Jump to a specific completed step (backward only, no validation) */
  goToStep: (step: number) => void;
  /**
   * Jump to any step without running validation. For programmatic skips —
   * e.g. auto-advancing past hidden steps, or clamping a hidden initialStep
   * to the first visible one. Direction is inferred from current vs target.
   */
  setStepDirect: (step: number) => void;
  /** Reset to step 0 */
  reset: () => void;
}

// ============================================
// HOOK
// ============================================

function useMultiStepForm({
  totalSteps,
  initialStep,
  onValidateStep,
}: UseMultiStepFormOptions): UseMultiStepFormReturn {
  const clampedInitial =
    initialStep != null
      ? Math.max(0, Math.min(totalSteps - 1, initialStep))
      : 0;
  const [currentStep, setCurrentStep] = useState(clampedInitial);
  const [direction, setDirection] = useState<1 | -1>(1);

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  const goToNext = useCallback(async (): Promise<boolean> => {
    if (currentStep >= totalSteps - 1) return false;

    if (onValidateStep) {
      const isValid = await onValidateStep(currentStep);
      if (!isValid) return false;
    }

    setDirection(1);
    setCurrentStep((prev) => prev + 1);
    return true;
  }, [currentStep, totalSteps, onValidateStep]);

  const goToPrev = useCallback(() => {
    if (currentStep <= 0) return;
    setDirection(-1);
    setCurrentStep((prev) => prev - 1);
  }, [currentStep]);

  const goToStep = useCallback(
    (step: number) => {
      if (step < 0 || step >= totalSteps || step === currentStep) return;
      // Only allow jumping to completed steps (backward navigation)
      if (step >= currentStep) return;
      setDirection(step < currentStep ? -1 : 1);
      setCurrentStep(step);
    },
    [currentStep, totalSteps],
  );

  const setStepDirect = useCallback(
    (step: number) => {
      if (step < 0 || step >= totalSteps) return;
      setCurrentStep((prev) => {
        if (prev === step) return prev;
        setDirection(step > prev ? 1 : -1);
        return step;
      });
    },
    [totalSteps],
  );

  const reset = useCallback(() => {
    setDirection(1);
    setCurrentStep(0);
  }, []);

  return {
    currentStep,
    isFirstStep,
    isLastStep,
    direction,
    goToNext,
    goToPrev,
    goToStep,
    setStepDirect,
    reset,
  };
}

export default useMultiStepForm;
